import { useState } from "react";
import { taskService } from "@miplatform/api";
import {
  DOCUMENT_TYPES,
  ENTITY_TYPES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IAttempt,
  IDocument,
  IDocumentCategoriesResponse,
  IStep,
  StepStatus,
} from "@miplatform/types";
import { externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";

export const useWorkOrderDocumentStepViewModel = ({
  attempt,
  step,
  onRefresh,
}: {
  attempt: IAttempt | null;
  step: IStep;
  onRefresh: () => void;
}) => {
  const {
    loading,
    uploadDocument,
    removeDocument,
    downloadFile,
    printFile,
    getDocumentCategories,
  } = useDocumentsViewModel();
  const [selectedDocument, setSelectedDocument] = useState<IDocument | null>(
    null
  );
  const [deleteFile, setDeleteFile] = useState<IDocument | null>(null);
  const [isDeleteModalOpen, handleDeleteModalOpen, handleDeleteModalClose] =
    useBoolean();
  const [documentCategories, setDocumentCategories] = useState<
    IDocumentCategoriesResponse[]
  >([]);

  const getDocumentCategory = async () => {
    const categories = await getDocumentCategories(ENTITY_TYPES.STEP);

    if (categories) {
      setDocumentCategories(categories);
    }
  };

  const setDeleteDocument = (document: IDocument) => {
    setDeleteFile(document);
    handleDeleteModalOpen();
  };

  const handleDocumentSelect = (document: IDocument | null) => {
    setSelectedDocument(document);
  };

  const handleResetSelectedDocument = () => {
    setSelectedDocument(null);
  };

  const uploadFiles = async (files: (File & { error?: string })[]) => {
    if (!files || files?.length === 0 || loading) return;

    const invalidFile = files.find((file) => file.error);

    if (invalidFile) {
      toastService.error({
        text: "File Upload Failed",
        subtext: invalidFile.error || "Please check your files and try again.",
      });
      return;
    }

    if (!step.stepResult) {
      const response = await taskService.assignments.updateStepResult({
        attemptId: attempt?.id,
        assignmentId: attempt?.assignment?.id,
        stepId: step.id,
        data: {
          status: StepStatus.Pending,
        },
        toastMessageConfig: {},
      });

      if (response.status === HTTP_STATUSES.CREATED) {
        step.stepResult = response.data;
      }
    }

    const uploadPromises = files.map((file) =>
      uploadDocument({
        file,
        documentType: DOCUMENT_TYPES.UNKNOWN,
        externalReferenceId: externalReferenceId.getWorkOrderStepResultRefId({
          workOrderId: attempt?.assignment?.workOrder?.workOrderId || "",
          assignmentId: attempt?.assignment?.id || "",
          attemptId: attempt?.id || "",
          stepResultId: step.stepResult?.id || "",
          stepId: step.id,
        }),
      })
    );
    const results = await Promise.all(uploadPromises);

    if (results.some((res) => res === null)) {
      toastService.error({
        text: "Upload Document Failed.",
      });
    } else {
      toastService.success({
        text: "Uploaded Document Successfully.",
      });
    }

    onRefresh();
  };

  const deleteDocument = async () => {
    const res = await removeDocument({
      id: deleteFile?.id || "",
      modifiedOn: deleteFile?.modifiedOn || "",
    });

    if (res) {
      toastService.success({
        text: "Deleted Document Successfully.",
      });
      onRefresh();
      handleDeleteModalClose();
    }
  };

  return {
    loading,
    isDeleteModalOpen,
    deleteFile,
    selectedDocument,
    documentCategories,
    downloadFile,
    printFile,
    uploadFiles,
    deleteDocument,
    setDeleteDocument,
    getDocumentCategory,
    handleDeleteModalClose,
    handleDocumentSelect,
    handleResetSelectedDocument,
  };
};
